import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { useCallback, useContext, useState } from 'react'
import { trigger } from '../../../lib/events'
import { ContextShell } from '../../../modules/context'
import { SongDropped, Task } from '../../../types/task.types'

interface UsePlaylist {
  addTaskToPlaylist(item: SongDropped): void
  deleteTaskFromPlaylist(trackOrders: number, playlistIdParam: string): void

  openModalConfirmation: boolean
  onDismissModalConfirmation(): void
  onConfirmAddTaskToPlaylist(item: SongDropped): void
}

export const usePlaylist = (): UsePlaylist => {
  const { i18n } = useLingui()
  const {
    MoisesCLI,
    toast: { add: addToast }
  } = useContext(ContextShell)

  const [taskId, setTaskId] = useState<string | null>(null)
  const [playlistId, setPlaylistId] = useState<string | null>(null)

  const [openModalConfirmation, setOpenedModalConfirmation] = useState(false)
  const onOpenModalConfirmation = useCallback(
    () => setOpenedModalConfirmation(true),
    []
  )
  const onDismissModalConfirmation = useCallback(
    () => setOpenedModalConfirmation(false),
    []
  )

  const onConfirmAddTaskToPlaylist = useCallback(
    async (songDropped?: SongDropped) => {
      onDismissModalConfirmation()

      try {
        if (!songDropped) {
          throw Error()
        }

        await MoisesCLI.addTrackToPlaylist({
          trackId: songDropped?.taskId || (taskId as string),
          playlistId: playlistId || songDropped?.playlistId
        })

        trigger('playlist:revalidate-tasks')
      } catch {
        // eslint-disable-next-line no-console
        console.error('Error adding task to playlist')
      }

      setTaskId(null)
      setPlaylistId(null)
      return true
    },
    [onDismissModalConfirmation, MoisesCLI, taskId, playlistId]
  )

  const addTaskToPlaylist = useCallback(
    async (songDropped: SongDropped) => {
      try {
        const { track } = (await MoisesCLI.track({
          id: songDropped.taskId
        })) as { track: Task }

        setTaskId(songDropped.taskId)
        setPlaylistId(songDropped.playlistId)

        const hasTrackOnPlaylist = track.playlists.find(
          ({ id }: { id: string }) => id === songDropped.playlistId
        )

        if (hasTrackOnPlaylist) {
          onOpenModalConfirmation()
        } else {
          onConfirmAddTaskToPlaylist(songDropped)
        }
      } catch {
        // eslint-disable-next-line no-console
        console.error('Error adding task to playlist')
      }
    },
    [MoisesCLI, onOpenModalConfirmation, onConfirmAddTaskToPlaylist]
  )

  const deleteTaskFromPlaylist = useCallback(
    async (trackOrders: number, playlistIdParam: string) => {
      try {
        await MoisesCLI.removeTrackFromPlaylist({
          playlistId: playlistIdParam,
          trackOrders
        })
      } catch (e: any) {
        addToast({
          icon: null,
          closable: true,
          type: 'error',
          title: i18n._(t`error.removing.song.playlist.title`),
          description: i18n
            ._(t`error.create.playlist.description`)
            .replace(/\*/g, '')
        })
      }

      return true
    },
    [addToast, MoisesCLI, i18n]
  )

  return {
    addTaskToPlaylist,
    deleteTaskFromPlaylist,
    openModalConfirmation,
    onConfirmAddTaskToPlaylist,
    onDismissModalConfirmation
  }
}
